import { memo, useCallback, useMemo } from 'react'
import { mutate } from 'swr'
import clsx from 'clsx'

import { Avatar, Box, ClickAwayListener, makeStyles } from '@material-ui/core'
import EditIcon from '@material-ui/icons/Create'
import ArrowForwardIcon from '@material-ui/icons/ArrowForward'
import FlagIcon from '@material-ui/icons/FlagOutlined'

import { getStepData, SpacedGroup, VisibilitySensor } from 'UI/Components'
import { StepTitle } from './StepTitle'
import { MagicDescription } from './MagicDescription'
import { StepDescription } from './StepDescription'
import { StepAudioNote } from './StepAudioNote'
import { StepScreenshot } from './StepScreenshot'
import { StepGoTo } from './StepGoTo'

import { QuickGuiddeType } from 'app/types'
import { useBoolean, useDataMutation } from 'hooks'

const useStyles = makeStyles({
    numberCircle: {
        marginTop: 2,
        maxWidth: 24,
        maxHeight: 24,
        color: '#000000',
        fontSize: '12px',
        fontWeight: 'bold'
    },
    highlightBorder: {
        borderRadius: 6,
        border: '2px solid #FED243 !important'
    },
    highlightBackground: {
        backgroundColor: '#FEFAE4',
        cursor: 'pointer'
    }
})

type Props = {
    index: number
    step: QuickGuiddeType
    playbookId: string
    canEdit: boolean
    showGoTo: boolean
    showMagicAnimation: boolean
    brandKitHighlightColor: string
}

export const Step = memo(
    ({
        index,
        canEdit,
        showGoTo,
        showMagicAnimation,
        brandKitHighlightColor,
        step,
        playbookId
    }: Props) => {
        const classes = useStyles()
        const { audioNote, note, title, kind } = step
        const { stepAvatar, stepGoTo, hasBgImage } = useMemo(() => getStepData(step), [step])

        const hover = useBoolean()
        const edit = useBoolean()

        const revalidate = useCallback(
            // be careful with this key, if one day we change url on PageViewPlaybook, this key should be updated as well
            () => mutate([`/c/v1/quickguidde?id=${playbookId}`, undefined]),
            [playbookId]
        )

        const $editDoc = useDataMutation('/c/v1/quickguidde/editDocStep', 'PUT', {
            onSuccess: revalidate
        })

        const updateStep = (
            updatedField: Partial<{
                title: string
                note: string
                url: string
                audioNote: QuickGuiddeType['audioNote']
                docScreenshot: string
            }>
        ) => {
            return $editDoc.mutate({ playbookId, stepId: step.id, ...updatedField })
        }

        const stepLabel = useMemo(() => {
            const labels = {
                cover: <ArrowForwardIcon fontSize="small" />,
                step: index.toString().padStart(2, '0'),
                end: <FlagIcon fontSize="small" />
            }

            if (canEdit && edit.isFalse && !note && kind !== 'step') {
                return (
                    <Box
                        px={1.5}
                        py={0.5}
                        borderRadius={12}
                        bgcolor={hover.isTrue ? '#FED243' : 'gainsboro'}
                    >
                        {labels[kind]} Add {kind === 'cover' ? 'opening' : 'closing'} paragraph
                    </Box>
                )
            }

            return (
                <Avatar
                    className={classes.numberCircle}
                    style={{ background: brandKitHighlightColor }}
                >
                    {labels[kind]}
                </Avatar>
            )
        }, [
            brandKitHighlightColor,
            canEdit,
            classes.numberCircle,
            edit.isFalse,
            hover.isTrue,
            index,
            kind,
            note
        ])

        // Do not render empty cover/end step for viewer
        if (!showMagicAnimation && !canEdit && kind !== 'step' && !note) return null

        return (
            <ClickAwayListener onClickAway={edit.setFalse}>
                <Box
                    // Please do not remove step.id, it is used as a scroll anchor in the QGNavigation
                    id={step.id}
                    mb={2}
                    px={4}
                    py={3}
                    width="100%"
                    style={{
                        border: '2px solid transparent'
                    }}
                    position="relative"
                    className={clsx({
                        [classes.highlightBorder]: hover.isTrue || edit.isTrue,
                        [classes.highlightBackground]: hover.isTrue && edit.isFalse
                    })}
                    onMouseEnter={canEdit ? () => hover.setTrue() : undefined}
                    onMouseLeave={canEdit ? () => hover.setFalse() : undefined}
                    onClick={canEdit ? () => edit.setTrue() : undefined}
                >
                    {hover.isTrue && edit.isFalse && (
                        <Box
                            p={1}
                            border="1px solid gainsboro"
                            borderRadius={4}
                            position="absolute"
                            top={16}
                            right={16}
                        >
                            <EditIcon fontSize="small" />
                        </Box>
                    )}

                    {showGoTo && (
                        <StepGoTo
                            isEditMode={edit.isTrue}
                            stepGoTo={stepGoTo}
                            link={step.url}
                            onUpdate={newLink => updateStep({ url: newLink })}
                        />
                    )}

                    <SpacedGroup
                        mb={2}
                        spacing={edit.isTrue ? 2 : 1}
                        flexDirection={edit.isTrue ? 'column' : 'row'}
                        alignItems="flex-start"
                    >
                        {stepLabel}

                        {/* Title field */}
                        {kind === 'step' && (
                            <StepTitle
                                isEditMode={edit.isTrue}
                                title={title}
                                onUpdate={newTitle => updateStep({ title: newTitle })}
                            />
                        )}
                    </SpacedGroup>

                    {/* Description field */}
                    {showMagicAnimation ? (
                        <VisibilitySensor
                            key={step.id}
                            placeholder={<span>&nbsp;</span>}
                            threshold={0.01}
                            delay={100}
                        >
                            <MagicDescription index={index} description={note} />
                        </VisibilitySensor>
                    ) : (
                        <StepDescription
                            isEditMode={edit.isTrue}
                            note={note}
                            onUpdate={newNote => updateStep({ note: newNote })}
                        />
                    )}

                    <VisibilitySensor
                        placeholder={<span>&nbsp;</span>}
                        threshold={0.01}
                        delay={100}
                    >
                        <>
                            {/* Step audio note */}
                            <StepAudioNote
                                isCover={kind === 'cover'}
                                isEditMode={edit.isTrue}
                                stepAvatar={stepAvatar}
                                audioNote={audioNote}
                                onUpdate={isIncludeTranscriptInDoc => {
                                    if (!step.audioNote) return Promise.resolve()
                                    return updateStep({
                                        audioNote: {
                                            ...step.audioNote,
                                            isIncludeTranscriptInDoc
                                        }
                                    })
                                }}
                            />

                            {kind === 'step' && hasBgImage && (
                                <StepScreenshot
                                    isEditMode={edit.isTrue}
                                    step={step}
                                    onUpdate={url => updateStep({ docScreenshot: url })}
                                />
                            )}
                        </>
                    </VisibilitySensor>
                </Box>
            </ClickAwayListener>
        )
    }
)
